/* eslint-disable no-console */
import { i18n } from 'i18next'
import * as R from 'ramda'

import { MomentLanguageMap } from '../constants/localization/moment'
import i18nUiComponents from '../locales/i18n'
import { updateMomentUiLocale } from '../momentLocale'
import {
  Constant,
  Language,
  LanguagesConstant,
  Nil,
  SentenceFormatter,
} from '../types'
import { findByName, getConstantsValue } from './index'

// @ts-ignore
// eslint-disable-next-line max-len
export const capitalize: <T extends string>(s: T) => Capitalize<T> = R.converge(
  R.concat,
  // @ts-ignore
  [R.compose(R.toUpper, R.head), R.compose(R.toLower, R.tail)],
)

// @ts-ignore
export const lowercaseFirst: <T extends string>(s: T) => string = R.converge(
  R.concat,
  [R.compose(R.toLower, R.head), R.tail],
)

export function getReadableEnumName(enumValue: string) {
  const capitalized: string = capitalize(enumValue.toLowerCase())
  return capitalized.replace(/_/g, ' ')
}

export const getSortedSupportedLanguages = (
  SupportedLngs: LanguagesConstant[],
  property = 'name' as keyof Pick<Language, 'code' | 'fullName' | 'name'>,
) =>
  R.sort((a: Language, b: Language) => a[property].localeCompare(b[property]))(
    SupportedLngs,
  )

export const getIsMultipleLanguagesSupported = (supportedLngsCodes: string[]) =>
  Array.isArray(supportedLngsCodes) && supportedLngsCodes.length > 1

const updateI18n = (i18nInstance: i18n | i18n[], languageValue: string) =>
  Array.isArray(i18nInstance)
    ? i18nInstance.forEach(instance => instance.changeLanguage(languageValue))
    : i18nInstance.changeLanguage(languageValue)

export const updateLanguage = (options: {
  SupportedLngsValuesList: string[]
  i18n?: i18n
  languageValue: string
}) => {
  const { languageValue, i18n: i18nInstance, SupportedLngsValuesList } = options

  if (R.isNil(i18nInstance)) {
    console.error('Instance of i18n is missing')
    return
  }

  if (!R.includes(languageValue, SupportedLngsValuesList)) {
    console.error(
      `${languageValue} language is not supported\nSupported languages : ${JSON.stringify(
        SupportedLngsValuesList,
      )}`,
    )
    return
  }

  if (
    languageValue === i18nInstance.language &&
    languageValue === i18nUiComponents.language
  ) {
    return
  }

  updateMomentUiLocale(MomentLanguageMap[languageValue])
  updateI18n([i18nInstance, i18nUiComponents], languageValue)
  window.location.reload()
}

export const getConstantTranslatedName = (
  id: string | Nil,
  list: Constant[],
  defValue = '',
): string => {
  const constant = getConstantsValue(id, list)
  return constant?.nameTranslation || constant?.name || defValue
}

export const getTranslatedFieldName = (
  obj: any,
  fieldName = 'name',
  fallback = '',
) => {
  if (!R.is(Object, obj)) {
    return fallback
  }

  const fieldNameTranslation = `${fieldName}Translation`

  const finalFieldName = R.ifElse(
    R.has(fieldNameTranslation),
    R.prop(fieldNameTranslation),
    R.prop(fieldName),
  )(obj)

  return R.is(String, finalFieldName) ? finalFieldName : fallback
}

export const getTranslatedNameByRawName = <T = Constant>(
  name: string,
  ConstantList: T[],
  fieldName = 'nameTranslation',
) =>
    R.pipe(findByName, r => getTranslatedFieldName(r, fieldName, name))(
      name,
      ConstantList,
    )

export const FormatterMap: Record<SentenceFormatter, (text: string) => string> =
  {
    [SentenceFormatter.REGULAR]: R.identity,
    [SentenceFormatter.CAPITALIZE]: capitalize,
    [SentenceFormatter.UPPER]: R.toUpper,
    [SentenceFormatter.LOWERCASE]: R.toLower,
  }

export const getSentence = (
  words: (number | string | Nil)[],
  formatter = SentenceFormatter.CAPITALIZE,
  joinWith = ' ',
) => R.pipe(R.filter(Boolean), R.join(joinWith), FormatterMap[formatter])(words)

export const isSentence = (sentence: string) => sentence.split(' ').length > 1

export const formatInitials = (sentence: string, joinWith = '') =>
  isSentence(sentence)
    ? R.pipe(
      R.split(' '),
      R.map(word => R.toUpper(R.head(word))),
      R.join(joinWith),
    )(sentence)
    : sentence
